<template>
  <div
    class="form-group"
    :class="{
      'form-group--has-error': localError && touched,
    }"
  >
    <label :for="id" v-if="label">
      {{ label }}
      <span v-if="required" class="form-group__required">*</span>
    </label>

    <div class="address-input">
      <div class="address-input__row">
        <div class="address-input__field address-input__field--street">
          <input
            :id="`${id}_street`"
            type="text"
            v-model="addressData.street"
            :placeholder="getTranslation('form.fields.address.street') || 'Straat'"
            class="address-input__input"
            @blur="handleBlur"
            @input="handleInput"
          />
        </div>
        <div class="address-input__field address-input__field--house-number">
          <input
            :id="`${id}_houseNumber`"
            type="text"
            v-model="addressData.houseNumber"
            :placeholder="getTranslation('form.fields.address.house_number') || 'Huisnummer'"
            class="address-input__input"
            @blur="handleBlur"
            @input="handleInput"
          />
        </div>
      </div>

      <div class="address-input__row">
        <div class="address-input__field address-input__field--postal-code">
          <input
            :id="`${id}_postalCode`"
            type="text"
            v-model="addressData.postalCode"
            :placeholder="getTranslation('form.fields.address.postal_code') || 'Postcode'"
            class="address-input__input"
            @blur="handleBlur"
            @input="handleInput"
          />
        </div>
        <div class="address-input__field address-input__field--city">
          <input
            :id="`${id}_city`"
            type="text"
            v-model="addressData.city"
            :placeholder="getTranslation('form.fields.address.city') || 'Stad'"
            class="address-input__input"
            @blur="handleBlur"
            @input="handleInput"
          />
        </div>
        <div class="address-input__field address-input__field--country">
          <select
            :id="`${id}_country`"
            v-model="addressData.country"
            class="address-input__input"
            @blur="handleBlur"
            @change="handleInput"
          >
            <option value="" disabled>
              {{ getTranslation('form.fields.address.select_country') || 'Land' }}
            </option>
            <option v-for="country in countries" :key="country.value" :value="country.value">
              {{ country.label }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <span v-if="localError && touched" class="form-group__error">{{ localError }}</span>
  </div>
</template>

<script>
  import { ref, watch, computed, getCurrentInstance } from 'vue';
  import { validateInput } from '@/utils/validation';
  import { COUNTRIES } from '@shared/constants/countries';

  export default {
    name: 'AddressInput',
    props: {
      id: {
        type: String,
        required: true,
      },
      label: {
        type: String,
        default: '',
      },
      modelValue: {
        type: Object,
        default: () => ({
          street: '',
          houseNumber: '',
          postalCode: '',
          city: '',
          country: '',
        }),
      },
      error: {
        type: String,
        default: '',
      },
      rules: {
        type: Array,
        default: () => [],
      },
      required: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['update:modelValue', 'validate', 'blur'],
    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance?.appContext?.config?.globalProperties?.$t;

      const getTranslation = (key) => {
        try {
          if ($t) {
            const translated = $t(key);
            // If translation returns the key itself, it means it wasn't found
            if (translated && translated !== key) {
              return translated;
            }
          }
        } catch (e) {
          // Translation not available
        }
        // Fallback translations (Dutch)
        const translations = {
          'form.fields.address.street': 'Straat',
          'form.fields.address.house_number': 'Huisnummer',
          'form.fields.address.postal_code': 'Postcode',
          'form.fields.address.city': 'Stad',
          'form.fields.address.select_country': 'Land',
        };
        return translations[key] || key;
      };

      const localError = ref(props.error);
      const touched = ref(false);
      const addressData = ref({
        street: props.modelValue?.street || '',
        houseNumber: props.modelValue?.houseNumber || '',
        postalCode: props.modelValue?.postalCode || '',
        city: props.modelValue?.city || '',
        country: props.modelValue?.country || '',
      });

      const handleInput = () => {
        emit('update:modelValue', { ...addressData.value });
        if (touched.value) {
          validate();
        }
      };

      const handleBlur = () => {
        touched.value = true;
        validate();
        emit('blur');
      };

      const validate = () => {
        // Check if address is required and empty
        if (props.required) {
          const hasAnyValue = Object.values(addressData.value).some((value) => value.trim() !== '');
          if (!hasAnyValue) {
            localError.value = 'Dit veld is verplicht';
            emit('validate', localError.value);
            return;
          }
        }

        // Validate individual fields if rules exist
        const errorMessage = validateInput(addressData.value, props.rules);
        localError.value = errorMessage;
        emit('validate', errorMessage);
      };

      watch(
        () => props.error,
        (newError) => {
          localError.value = newError;
        },
        { immediate: true }
      );

      watch(
        () => props.modelValue,
        (newValue) => {
          if (newValue) {
            addressData.value = {
              street: newValue.street || '',
              houseNumber: newValue.houseNumber || '',
              postalCode: newValue.postalCode || '',
              city: newValue.city || '',
              country: newValue.country || '',
            };
          }
        },
        { deep: true }
      );

      return {
        localError,
        touched,
        addressData,
        handleInput,
        handleBlur,
        validate,
        getTranslation,
        countries: COUNTRIES,
      };
    },
  };
</script>

<style scoped lang="scss">
  .address-input {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__row {
      display: grid;
      grid-template-columns: 1fr 150px;
      gap: 1rem;

      &:nth-child(2) {
        grid-template-columns: 200px 1fr 180px;
      }

      @media (max-width: 768px) {
        grid-template-columns: 1fr;

        &:nth-child(2) {
          grid-template-columns: 1fr;
        }
      }
    }

    &__field {
      &--street {
        grid-column: 1;
      }

      &--house-number {
        grid-column: 2;
        max-width: 150px;

        @media (max-width: 768px) {
          grid-column: 1;
          max-width: 100%;
        }
      }

      &--postal-code {
        grid-column: 1;
        max-width: 200px;

        @media (max-width: 768px) {
          grid-column: 1;
          max-width: 100%;
        }
      }

      &--city {
        grid-column: 2;

        @media (max-width: 768px) {
          grid-column: 1;
        }
      }

      &--country {
        grid-column: 3;
        max-width: 180px;

        @media (max-width: 768px) {
          grid-column: 1;
          max-width: 100%;
        }
      }
    }

    &__input {
      width: 100%;
      padding: 0.5rem 0.75rem;
      border: 1px solid var(--slate-300);
      border-radius: 0.375rem;
      font-size: 0.875rem;
      transition:
        border-color 0.2s ease,
        box-shadow 0.2s ease;
      background-color: white;
      color: var(--slate-900);

      &:focus {
        outline: none;
        border-color: var(--brand-500);
        box-shadow: 0 0 0 3px rgba(var(--brand-500-rgb), 0.1);
      }

      &::placeholder {
        color: var(--slate-400);
      }

      // Select styling
      &[type='select'],
      select & {
        cursor: pointer;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 0.75rem center;
        padding-right: 2.5rem;

        option {
          color: var(--slate-900);
          background-color: white;
        }

        option:disabled {
          color: var(--slate-400);
        }
      }
    }
  }

  .form-group {
    &__required {
      color: var(--red-500);
      margin-left: 0.25rem;
    }

    &__error {
      display: block;
      color: var(--red-500);
      font-size: 0.875rem;
      margin-top: 0.5rem;
    }

    &--has-error {
      .address-input__input {
        border-color: var(--red-500);
      }
    }
  }
</style>
